<script setup lang="ts">
import {
  type Field,
  type FieldSource,
  SOURCE_META,
  SOURCE_ORDER,
} from "../../../content/reports/ranked-explorer/fields";

const props = defineProps<{
  open: boolean;
  mode: "filter" | "column" | "any";
  fields: Field[];
  usedFilters: Set<string>;
  usedColumns: Set<string>;
}>();

const emit = defineEmits<{
  (e: "update:open", value: boolean): void;
  (e: "pick", field: Field, role: "filter" | "column"): void;
}>();

const query = ref("");
const sourceFilter = ref<FieldSource | "all">("all");

const sourceOptions = computed<Array<FieldSource | "all">>(() => [
  "all",
  ...SOURCE_ORDER,
]);

const sourceCounts = computed(() => {
  const c: Record<string, number> = { all: props.fields.length };

  for (const f of props.fields) {
    c[f.source] = (c[f.source] || 0) + 1;
  }

  return c;
});

const filtered = computed(() => {
  const q = query.value.trim().toLowerCase();

  return props.fields.filter((f: Field) => {
    if (sourceFilter.value !== "all" && f.source !== sourceFilter.value) return false;
    if (!q) return true;
    if (f.label.toLowerCase().includes(q)) return true;
    if (f.ref.toLowerCase().includes(q)) return true;
    if (f.options?.some((o: { id: string; name: string }) => o.name.toLowerCase().includes(q))) return true;

    return false;
  });
});

const groups = computed(() => {
  const m: Record<string, Field[]> = {};

  for (const f of filtered.value) {
    (m[f.source] = m[f.source] || []).push(f);
  }

  return SOURCE_ORDER.filter((s) => m[s]).map((s) => ({
    source: s,
    items: m[s],
  }));
});

function usedRole(f: Field): string | null {
  const isFilter = props.usedFilters.has(f.id);
  const isColumn = props.usedColumns.has(f.id);

  if (isFilter && isColumn) return "both";
  if (isFilter) return "filter";
  if (isColumn) return "column";

  return null;
}

function pick(field: Field, role: "filter" | "column") {
  emit("pick", field, role);
}

watch(
  () => props.open,
  (v) => {
    if (v) {
      query.value = "";
      sourceFilter.value = "all";
    }
  }
);
</script>

<template>
  <Dialog :open="open" @update:open="emit('update:open', $event)">
    <DialogContent class="w-[1024px] overflow-hidden p-0">
      <DialogHeader class="sr-only">
        <DialogTitle>
          {{ mode === "filter" ? "Add facet filter" : mode === "column" ? "Add display column" : "Add field" }}
        </DialogTitle>
      </DialogHeader>

      <!-- Search -->
      <div class="flex items-center gap-2.5 border-b px-3.5 py-3 pr-10">
        <Icon mode="svg" size="15" name="lucide:search" class="shrink-0 text-muted-foreground" />

        <input v-model="query"
          class="min-w-0 flex-1 bg-transparent text-[13px] outline-none placeholder:text-muted-foreground"
          :placeholder="`Search ${fields.length} fields…`" autofocus />
      </div>

      <!-- Source chips -->
      <div class="flex flex-wrap gap-1.5 border-b px-3.5 py-2.5">
        <button v-for="s in sourceOptions" :key="s"
          class="inline-flex h-6 cursor-pointer items-center gap-1 rounded-full px-2.5 text-[11px] font-medium transition-colors"
          :class="sourceFilter === s
              ? 'bg-primary text-primary-foreground'
              : 'border text-muted-foreground hover:text-foreground'
            " @click="sourceFilter = s">
          <Icon v-if="s !== 'all' && SOURCE_META[s]" mode="svg" size="11" :name="SOURCE_META[s].icon" />

          {{ s === "all" ? "All" : SOURCE_META[s]?.label }}

          <span class="opacity-60">{{ sourceCounts[s] || 0 }}</span>
        </button>
      </div>

      <!-- Field list -->
      <ScrollArea class="h-96 w-full">
        <p v-if="!filtered.length" class="py-10 text-center text-sm text-muted-foreground">
          No fields match "{{ query }}".
        </p>

        <div v-for="g in groups" :key="g.source">
          <div
            class="flex items-center gap-1.5 px-2.5 pb-1.5 pt-3 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
            <Icon mode="svg" size="12" :name="SOURCE_META[g.source].icon" />
            {{ SOURCE_META[g.source].label }}
            <span class="opacity-60">· {{ g.items.length }}</span>
          </div>

          <div v-for="f in g.items" :key="f.id"
            class="flex cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2.5 hover:bg-accent"
            @click="pick(f, mode === 'any' ? 'filter' : mode)">
            <span class="flex size-6 shrink-0 items-center justify-center rounded-md" :style="{
              background: `color-mix(in oklch, ${SOURCE_META[f.source].color}, transparent 86%)`,
              color: SOURCE_META[f.source].color,
            }">
              <Icon mode="svg" size="13" :name="SOURCE_META[f.source].icon" />
            </span>

            <div class="min-w-0 flex-1">
              <div class="flex items-center gap-1.5">
                <span class="truncate text-[13px] font-medium">{{ f.label }}</span>

                <span
                  class="inline-flex h-4 shrink-0 items-center rounded bg-muted px-1.5 text-[10px] capitalize text-muted-foreground">
                  {{ f.kind }}
                </span>

                <Badge v-if="usedRole(f)" variant="outline" class="h-4 shrink-0 px-1 text-[9px]">
                  {{ usedRole(f) === "both" ? "in use" : usedRole(f) }}
                </Badge>
              </div>

              <div v-if="f.kind === 'options' && f.options?.length" class="mt-1 flex flex-wrap gap-1">
                <span v-for="o in f.options.slice(0, 3)" :key="o.id"
                  class="max-w-[100px] truncate rounded-full bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                  {{ o.name }}
                </span>

                <span v-if="(f.options?.length ?? 0) > 3" class="self-center text-[10px] text-muted-foreground">
                  +{{ (f.options?.length ?? 0) - 3 }}
                </span>
              </div>
            </div>

            <div class="flex shrink-0 gap-1.5" @click.stop>
              <template v-if="mode === 'any'">
                <button
                  class="inline-flex size-7 cursor-pointer items-center justify-center rounded-md transition-colors"
                  :class="usedFilters.has(f.id)
                      ? 'bg-primary text-primary-foreground'
                      : 'border text-muted-foreground hover:text-foreground'
                    " title="Add as filter" @click="pick(f, 'filter')">
                  <Icon mode="svg" size="14" :name="usedFilters.has(f.id) ? 'lucide:check' : 'lucide:filter'" />
                </button>

                <button
                  class="inline-flex size-7 cursor-pointer items-center justify-center rounded-md transition-colors"
                  :class="usedColumns.has(f.id)
                      ? 'bg-primary text-primary-foreground'
                      : 'border text-muted-foreground hover:text-foreground'
                    " title="Add as column" @click="pick(f, 'column')">
                  <Icon mode="svg" size="14" :name="usedColumns.has(f.id) ? 'lucide:check' : 'lucide:columns-3'" />
                </button>
              </template>

              <template v-else>
                <button
                  class="inline-flex size-7 cursor-pointer items-center justify-center rounded-md transition-colors"
                  :class="usedRole(f) && (usedRole(f) === mode || usedRole(f) === 'both')
                      ? 'bg-primary text-primary-foreground'
                      : 'border text-muted-foreground hover:text-foreground'
                    " :title="usedRole(f) && (usedRole(f) === mode || usedRole(f) === 'both') ? 'Added' : 'Add'"
                  @click="pick(f, mode)">
                  <Icon mode="svg" size="14" :name="usedRole(f) && (usedRole(f) === mode || usedRole(f) === 'both')
                      ? 'lucide:check'
                      : 'lucide:plus'
                    " />
                </button>
              </template>
            </div>
          </div>
        </div>
      </ScrollArea>

      <!-- Footer -->
      <div class="flex items-center gap-4 border-t px-3.5 py-2 text-[11px] text-muted-foreground">
        <span>
          {{ mode === "filter" ? "Add facet filter" : mode === "column" ? "Add display column" : "Add field" }}
        </span>
      </div>
    </DialogContent>
  </Dialog>
</template>